import { useState, useRef, useEffect, useCallback } from 'react'
import { VisualState, VisualShape, TranscriptItem } from 'types'
import { ModelRoute } from '../../logic/smartRouting.js' // Or where ModelRoute is defined, likely App.tsx or types
import { detectVisualIntent, IntentContext } from '../../logic/visualIntent.js'
import { shapeMorpher } from '../../../utils/visuals/morph.js'
import { clientSessionViewStore } from '../../../services/clientSessionViewStore.js'
import { calculateReasoningComplexity } from '../../logic/visualComplexity.js'
import type { CanvasSurface } from '../../lib/chat-canvas-shapes.js'
import { resolveDominantCanvasShape } from '../../lib/canvas-shape-resolver.js'

// Need to match ModelRoute definition from App.tsx/types
// Ideally ModelRoute should be in types.ts, but for now assuming it's passed in

interface UseVisualStateProps {
  transcript: TranscriptItem[]
  activeRoute: ModelRoute
  isWebcamActiveRef: React.MutableRefObject<boolean>
  canvasSurface?: CanvasSurface
}

export function useVisualState({
  transcript,
  activeRoute,
  isWebcamActiveRef,
  canvasSurface = 'landing',
}: UseVisualStateProps) {
  const volumeUpdateRef = useRef({
    lastAt: 0,
    lastMode: 'idle' as VisualState['mode'],
    lastAudioLevel: 0,
  })
  const [visualState, setVisualState] = useState<VisualState>({
    isActive: false,
    audioLevel: 0,
    mode: 'idle',
    // Landing opens on search grit; chat keeps presence orb until voice/tools fire.
    shape: canvasSurface === 'landing' ? 'constellation' : 'orb',
    // MUST be false when mode is 'idle' — the resolver sets it true when active.
    // Setting true here caused getUserMedia to fire on first render (5x recurring bug).
    waveformEnabled: false,
    waveformMode: 'oscilloscope',
  })

  const visualStateRef = useRef(visualState)
  useEffect(() => { visualStateRef.current = visualState }, [visualState])

  const semanticShapeRef = useRef<VisualShape>(canvasSurface === 'landing' ? 'constellation' : 'orb')
  const weatherDataRef = useRef<VisualState['weatherData']>(undefined)
  const chartDataRef = useRef<VisualState['chartData']>(undefined)
  const mapDataRef = useRef<VisualState['mapData']>(undefined)
  const textContentRef = useRef<string | undefined>(undefined)
  const morphProgressRef = useRef<number>(0)
  const researchDataRef = useRef<VisualState['researchData']>(undefined)
  const toolCallDataRef = useRef<VisualState['toolCallData']>(undefined)
  const syncVisualShape = useCallback(
    (targetShape: VisualShape, mode: VisualState['mode']) => {
      const hasResearch = !!researchDataRef.current
      const hasRunningTool = toolCallDataRef.current?.state === 'running'
      const snapshot = clientSessionViewStore.getSnapshot()
      const lastModelItem = [...snapshot.transcript].reverse().find(item => item.role === 'model')
      const sourceCount = lastModelItem?.groundingMetadata?.webSearchQueries?.length ?? 0
      const citationCount = lastModelItem?.groundingMetadata?.groundingChunks?.length ?? 0
      const resolved = resolveDominantCanvasShape(
        targetShape,
        mode,
        activeRoute,
        isWebcamActiveRef.current,
        {
          hasResearch,
          hasRunningTool,
          sourceCount,
          citationCount,
          preferredWaveform: mode === 'speaking' ? 'oscilloscope' : 'spectrum',
          ...(toolCallDataRef.current?.name
            ? { runningToolName: toolCallDataRef.current.name }
            : {}),
        }
      )

      setVisualState(prev => {
        const newShape = resolved.shape
        const currentShape = prev.shape

        // Trigger morph when shape changes (e.g., volume mode transitions)
        if (newShape !== currentShape && prev.morphingTo !== newShape) {
          shapeMorpher.morph(currentShape, newShape)
        }

        const update = {
          ...prev,
          shape: newShape,
          mode,
          waveformEnabled: resolved.waveformEnabled,
          waveformMode: resolved.waveformMode,
          researchActive: resolved.researchActive,
          morphingTo: newShape !== currentShape ? newShape : prev.morphingTo,
          morphProgress: shapeMorpher.getProgress(),
        }
        if (textContentRef.current !== undefined)
          (update as Record<string, unknown>).textContent = textContentRef.current
        if (weatherDataRef.current !== undefined)
          (update as Record<string, unknown>).weatherData = weatherDataRef.current
        if (chartDataRef.current !== undefined)
          (update as Record<string, unknown>).chartData = chartDataRef.current
        if (mapDataRef.current !== undefined)
          (update as Record<string, unknown>).mapData = mapDataRef.current
        if (researchDataRef.current !== undefined)
          (update as Record<string, unknown>).researchData = researchDataRef.current
        if (toolCallDataRef.current !== undefined)
          (update as Record<string, unknown>).toolCallData = toolCallDataRef.current
        return update as VisualState
      })
    },
    [activeRoute, isWebcamActiveRef]
  )

  // React to transcript changes
  useEffect(() => {
    const last = transcript[transcript.length - 1]

    // Detect research activity from real grounding metadata (web search queries)
    const gm = last?.groundingMetadata
    const hasRealResearch = (gm?.webSearchQueries?.length ?? 0) > 0

    if (hasRealResearch) {
      const queryCount = gm!.webSearchQueries!.length
      const chunkCount = gm?.groundingChunks?.length ?? 0
      // Intensity scales with number of queries and grounding chunks
      const intensity = Math.min(queryCount * 0.3 + chunkCount * 0.1, 1.0)
      researchDataRef.current = {
        activeQueries: queryCount,
        intensity,
      }
    } else if (!last?.isFinal && researchDataRef.current?.activeQueries) {
      // Keep existing research data while streaming (don't clear mid-response)
    } else if (last?.isFinal) {
      // Clear research data when response is final and no grounding present
      researchDataRef.current = undefined
    }

    // Detect tool calls
    if (last?.tools && last.tools.length > 0) {
      const activeTool = last.tools.find(t => t.state === 'running')
      if (activeTool) {
        toolCallDataRef.current = {
          name: activeTool.name,
          progress: 0.5, // Default progress
          state: activeTool.state || 'running',
        }
        const toolName = activeTool.name.toLowerCase()
        let toolShape: VisualShape = 'toolCall'
        if (toolName.includes('weather')) toolShape = 'orb'
        else if (toolName.includes('screen')) toolShape = 'screenshare'
        else if (toolName.includes('webcam') || toolName.includes('camera')) toolShape = 'webcam'
        else if (toolName.includes('booking') || toolName.includes('calendar'))
          toolShape = 'functionPulse'
        syncVisualShape(toolShape, visualStateRef.current.mode === 'idle' ? 'thinking' : visualStateRef.current.mode)
      } else if (toolCallDataRef.current?.state === 'running') {
        toolCallDataRef.current = {
          ...toolCallDataRef.current,
          state: 'complete',
          progress: 1,
        }
      }
    }

    if (last?.role === 'user' && last.text?.trim()) {
      const result = detectVisualIntent(last.text, { transcript, surface: canvasSurface })
      if (result?.shape) {
        semanticShapeRef.current = result.shape
        if (result.textContent) textContentRef.current = result.textContent
        if (result.weatherData) weatherDataRef.current = result.weatherData
        if (result.chartData) chartDataRef.current = result.chartData
        if (result.mapData) mapDataRef.current = result.mapData
        syncVisualShape(result.shape, 'thinking')
      } else if (canvasSurface === 'chat' && /\b(weather|forecast|temperature)\b/i.test(last.text)) {
        syncVisualShape('orb', 'thinking')
      }
    } else if (
      last?.role === 'model' &&
      last?.reasoning &&
      (last.status === 'streaming' || !last.isFinal)
    ) {
      const complexity = calculateReasoningComplexity(last.reasoning)
      if (semanticShapeRef.current !== 'constellation') {
        semanticShapeRef.current = 'constellation'
        syncVisualShape('constellation', 'thinking')
        setVisualState(prev => ({ ...prev, reasoningComplexity: complexity }))
      } else {
        // Update complexity as reasoning streams in
        setVisualState(prev => ({ ...prev, reasoningComplexity: complexity }))
      }
    } else if (last?.role === 'model' && last?.isFinal) {
      const groundingMeta = last.groundingMetadata
      const snapshot = clientSessionViewStore.getSnapshot()
      const location = snapshot.location

      const context: IntentContext = {
        ...(groundingMeta ? { groundingMetadata: groundingMeta } : {}),
        ...(location
          ? { location: { latitude: location.latitude, longitude: location.longitude } }
          : {}),
        transcript: transcript,
        surface: canvasSurface,
      }

      const result = detectVisualIntent(last.text, context)
      if (result && result.shape) {
        semanticShapeRef.current = result.shape

        // Update refs with extracted data
        if (result.textContent) textContentRef.current = result.textContent
        if (result.weatherData) weatherDataRef.current = result.weatherData
        if (result.chartData) chartDataRef.current = result.chartData
        if (result.mapData) mapDataRef.current = result.mapData

        // Track citation counts from real grounding data
        const citationCount = groundingMeta?.groundingChunks?.length || 0
        const sourceCount = groundingMeta?.webSearchQueries?.length || 0

        // Calculate final reasoning complexity from completed reasoning
        const reasoningComplexity = calculateReasoningComplexity(last.reasoning)

        const newShape = result.shape
        const currentShape = visualStateRef.current.shape

        // Start morphing if shape is different and morphing is enabled
        if (newShape !== currentShape && visualStateRef.current.morphingTo !== newShape) {
          shapeMorpher.morph(currentShape, newShape)
        }

        const resolved = resolveDominantCanvasShape(
          newShape,
          'speaking',
          activeRoute,
          isWebcamActiveRef.current,
          {
            hasResearch: !!researchDataRef.current,
            hasRunningTool: toolCallDataRef.current?.state === 'running',
            sourceCount,
            citationCount,
            preferredWaveform: 'oscilloscope',
            ...(toolCallDataRef.current?.name
              ? { runningToolName: toolCallDataRef.current.name }
              : {}),
          }
        )

        setVisualState(prev => {
          const update: Partial<VisualState> = {
            ...prev,
            shape: resolved.shape,
            waveformEnabled: resolved.waveformEnabled,
            waveformMode: resolved.waveformMode,
            researchActive: resolved.researchActive,
            morphingTo: shapeMorpher.isActive() ? resolved.shape : null,
            morphProgress: shapeMorpher.getProgress(),
            citationCount,
            sourceCount,
            reasoningComplexity,
          }

          if (textContentRef.current !== undefined) update.textContent = textContentRef.current
          if (weatherDataRef.current !== undefined) update.weatherData = weatherDataRef.current
          if (chartDataRef.current !== undefined) update.chartData = chartDataRef.current
          if (mapDataRef.current !== undefined) update.mapData = mapDataRef.current
          if (researchDataRef.current !== undefined) update.researchData = researchDataRef.current
          if (toolCallDataRef.current !== undefined) update.toolCallData = toolCallDataRef.current

          return update as VisualState
        })
      }
    }
  }, [
    activeRoute,
    isWebcamActiveRef,
    syncVisualShape,
    transcript,
    canvasSurface,
  ])

  // Update morphing progress — runs whenever a morph starts (morphingTo changes)
  useEffect(() => {
    if (!visualState.morphingTo) return

    let rafId = 0

    const tick = () => {
      shapeMorpher.update(performance.now())
      const progress = shapeMorpher.getProgress()

      if (progress !== morphProgressRef.current) {
        morphProgressRef.current = progress
        setVisualState(prev => ({
          ...prev,
          morphProgress: progress,
          ...(progress >= 1 && { morphingTo: null }),
        }))
      }

      if (shapeMorpher.isActive()) {
        rafId = requestAnimationFrame(tick)
      }
    }

    rafId = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafId)
  }, [visualState.morphingTo])

  const handleVolumeChange = useCallback(
    (inputVol: number, outputVol: number) => {
      setVisualState(prev => {
        const micLevel = Math.min(inputVol * 3.0, 1.0)
        const speakerLevel = Math.min(outputVol * 3.0, 1.0)

        let mode: 'idle' | 'listening' | 'thinking' | 'speaking' = prev.mode
        let activeLevel = 0

        if (!prev.isActive) {
          mode = 'idle'
          activeLevel = 0
        } else if (speakerLevel > 0.01) {
          mode = 'speaking'
          activeLevel = speakerLevel
        } else if (micLevel > 0.02) {
          mode = 'listening'
          activeLevel = micLevel
        } else {
          mode = 'thinking'
          activeLevel = 0.1
        }

        const now = Date.now()
        const volumeState = volumeUpdateRef.current
        const modeChanged = volumeState.lastMode !== mode
        const levelDelta = Math.abs(volumeState.lastAudioLevel - activeLevel)
        const shouldSkipUpdate = !modeChanged && levelDelta < 0.04 && now - volumeState.lastAt < 100

        if (shouldSkipUpdate) {
          return prev
        }

        volumeState.lastAt = now
        volumeState.lastMode = mode
        volumeState.lastAudioLevel = activeLevel

        let shape: VisualShape = semanticShapeRef.current

        if (prev.shape === 'text' && (mode === 'speaking' || mode === 'thinking')) {
          // Relax the text shape lock if a new semantic shape is pending from the transcript
          if (semanticShapeRef.current !== 'text' && semanticShapeRef.current !== 'wave') {
            shape = semanticShapeRef.current
          } else {
            shape = 'text'
          }
        } else if (mode === 'speaking') {
          const contentShapes: VisualShape[] = ['text', 'weather', 'chart', 'map', 'code']
          if (
            !contentShapes.includes(semanticShapeRef.current) &&
            (['orb', 'wave', 'brain', 'idle'].includes(prev.shape) ||
              prev.shape === semanticShapeRef.current)
          ) {
            shape = 'wave'
          }
        } else if (mode === 'listening') {
          if (['wave', 'brain'].includes(prev.shape)) {
            shape = 'orb'
          }
        } else if (mode === 'thinking') {
          if (['orb', 'wave'].includes(prev.shape)) {
            shape = 'orb'
          }
        }

        const resolved = resolveDominantCanvasShape(
          shape,
          mode,
          activeRoute,
          isWebcamActiveRef.current,
          {
            hasResearch: !!researchDataRef.current,
            hasRunningTool: toolCallDataRef.current?.state === 'running',
            sourceCount: prev.sourceCount || 0,
            citationCount: prev.citationCount || 0,
            preferredWaveform: mode === 'speaking' ? 'oscilloscope' : 'spectrum',
            ...(toolCallDataRef.current?.name
              ? { runningToolName: toolCallDataRef.current.name }
              : {}),
          }
        )

        return {
          ...prev,
          audioLevel: activeLevel,
          mode: mode,
          shape: resolved.shape,
          waveformEnabled: resolved.waveformEnabled,
          waveformMode: resolved.waveformMode,
          researchActive: resolved.researchActive,
          ...(textContentRef.current !== undefined && { textContent: textContentRef.current }),
          ...(weatherDataRef.current !== undefined && { weatherData: weatherDataRef.current }),
          ...(chartDataRef.current !== undefined && { chartData: chartDataRef.current }),
          ...(mapDataRef.current !== undefined && { mapData: mapDataRef.current }),
          ...(researchDataRef.current !== undefined && { researchData: researchDataRef.current }),
          ...(toolCallDataRef.current !== undefined && { toolCallData: toolCallDataRef.current }),
        }
      })
    },
    [activeRoute, isWebcamActiveRef]
  )

  return {
    visualState,
    setVisualState,
    semanticShapeRef,
    handleVolumeChange,
    mapDataRef, // Exposed for external updates (e.g. from handleTranscript)
    weatherDataRef,
    chartDataRef,
    textContentRef,
  }
}
